import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { ConditionExpression } from 'app/model/dataflow/condition-expression';
import { Owned } from 'app/model/base/named';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { Transformation } from 'app/model/dataflow/transformation';
import { StringDataFlowType, BooleanDataFlowType, IntegerDataFlowType, TimeStampDataFlowType, ArrayDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { Antlr4Utils } from './antlr4-utils';
import { DataFlowUtils } from 'app/authoring/component/transformations/dataFlow-utils';
import { DataFlowParameterConfig } from 'shell-models-lib';
import { DataFlowAdapterUtils } from 'app/model/adapters/dataflow/adapter.util';
import { DataFlowExpressionArray } from 'app/model/dataflow/dataflow-expression-array';
import { isMap } from 'lodash';

export interface IStringWrapper {
    value: string;
}

export class DataFlowExpressionUtils {
    private static _ERROR_FUNCTION_CONSTANT = 'ERROR_FUNCTION(\'';
    private static _NEW_LINE_N_CONSTANT = '__NEW_LINE_N__';
    private static _NEW_LINE_R_CONSTANT = '__NEW_LINE_R__';
    private static _SINGLE_QUOTE_CONSTANT = '__SINGLE_QUOTE__';

    public static wrapExpressionWithErrors(expressionValue: string, typeDefinition?: boolean): string {
        const formattedExpression = `${typeDefinition ? 'columnType ? ' : ''}${expressionValue}`;
        if (Antlr4Utils.hasSyntaxErrorsInExpression(formattedExpression)) {
            return `${DataFlowExpressionUtils._ERROR_FUNCTION_CONSTANT}${DataFlowExpressionUtils._escapeNewLineChars(expressionValue)}')`;
        }
        return expressionValue;
    }

    public static unwrapExpressionWithErrors(expressionValue: string): string {
        if (expressionValue && expressionValue.startsWith(DataFlowExpressionUtils._ERROR_FUNCTION_CONSTANT)) {
            return DataFlowExpressionUtils._unEscapeNewLineChars(
                expressionValue.substring(DataFlowExpressionUtils._ERROR_FUNCTION_CONSTANT.length, expressionValue.length - 2));
        }

        return expressionValue;
    }

    public static wrapExpressionWithEmptyValue(expressionValue: string): string {
        if (stringIsNullOrEmpty(expressionValue)) {
            return `${DataFlowExpressionUtils._ERROR_FUNCTION_CONSTANT}')`;
        }
        return expressionValue;
    }

    public static unwrapExpressionWithEmptyValue(expressionValue: string): string {
        if (expressionValue && expressionValue.startsWith(DataFlowExpressionUtils._ERROR_FUNCTION_CONSTANT)) {
            return DataFlowExpressionUtils._unEscapeNewLineChars(
                expressionValue.substring(DataFlowExpressionUtils._ERROR_FUNCTION_CONSTANT.length, expressionValue.length - 2));
        }

        return expressionValue;
    }

    public static getConditionalExpressionScript(conditionExpressions: ConditionExpression[]): string {
        const conditionalExpressionList = conditionExpressions.map((expression) => {
            return `${DataFlowExpressionUtils.wrapExpressionWithEmptyValue(expression.leftExpression.expression)}` +
                ` ${expression.conditionType} ${DataFlowExpressionUtils.wrapExpressionWithEmptyValue(expression.rightExpression.expression)}`;
        });

        return conditionalExpressionList.join('\n\t&& ');
    }

    /*
        TODO: yuex
        here we don't consider the scenario that the inner expression contains escaped quote \'. for example,
        we escape the expression `b == 'string \'a''`, we should escape the ' to \', but we shoud not convert the exisiting \'
        to \\'. this scenario will be considered after the behavior settles down.
    */
    public static escapeSingleQuoteInDslString(str: string): string {
        return str.replace(/'/g, '\\\'');
    }

    public static unescapeSingleQuoteInDslString(str: string): string {
        return str.replace(/\\'/g, '\'');
    }

    public static escapeAllNewlineInDslString(str: string): string {
        return str.replace(/\n/g, '\\n').replace(/\r/g, '\\r');
    }

    public static unescapeAllNewlineInDslString(str: string): string {
        return str.replace(/\\n/g, '\n').replace(/\\r/g, '\r');
    }

    public static escapeNewlineInDslString(str: string): string {
        return str.replace(/\n/g, '\\n');
    }

    public static escapeTabInDslString(str: string): string {
        return str.replace(/\t/g, '\\t');
    }

    public static escapeHeadingStartInDslString(str: string): string {
        return str.replace(/\u0001/g, '\\u0001');
    }

    public static escapeBackslashInDslString(str: string): string {
        return str.replace(/\\/g, '\\\\');
    }

    public static unescapeBackslashInDslString(str: string): string {
        return str.replace(/\\\\/g, '\\');
    }

    public static unescapeNewlineInDslString(str: string): string {
        return str.replace(/\\n/g, '\n');
    }

    public static containsNewlineInDslString(str: string): boolean {
        return /\\n/g.test(str);
    }

    public static containsUnescapedAllNewLineInString(str: string): boolean {
        return /\n/g.test(str) || /\r/g.test(str);
    }

    public static containsUnescapedTabInString(str: string): boolean {
        return /\t/g.test(str);
    }

    public static containsUnescapedHeadingStartInString(str: string): boolean {
        return /\u0001/g.test(str);
    }

    public static trimSpacesAndQuotesInDslString(str: string): string {
        return str.replace(/^\s*'/, '').replace(/'\s*$/, '');
    }

    public static trimSpacesAndAllQuotesInDslString(str: string): string {
        // Replace whitespace
        str.replace(/^\s*/, '').replace(/\s*$/, '');
        // Replace single quotes or double quotes
        return str.startsWith('\'') && str.endsWith('\'') ? str.replace(/^'/, '').replace(/'$/, '') : str.replace(/^"/, '').replace(/"$/, '');
    }

    public static validateDSLArray(strArr: IStringWrapper): string {
        // trim
        strArr.value = strArr.value.trim();
        // remove '[' and ']'
        if (strArr.value.startsWith('[') && strArr.value.endsWith(']')) {
            strArr.value = strArr.value.slice(1, strArr.value.length - 1);
        } else {
            throw new Error('invalid array, should start with "[" and end with "]"');
        }
        return strArr.value;
    }

    public static parseStringArrayInDsl(str: string): string[] {
        const strArr: IStringWrapper = {
            value: str
        };
        const result: string[] = [];
        strArr.value = DataFlowExpressionUtils.validateDSLArray(strArr);
        // loop for fetching each string
        while (strArr.value.length > 0) {
            result.push(DataFlowExpressionUtils.parseNextValueFromADsl(strArr));
        }
        return result;
    }

    /**
     * parse string like '[
     *  [],
     *  (),
     *  {}
     * ]'
     * @param str
     */
    public static parseScopeArrayInDsl(str: string): string[] {
        const strArr: IStringWrapper = {
            value: str
        };
        const result: string[] = [];
        strArr.value = DataFlowExpressionUtils.validateDSLArray(strArr);
        // loop for fetching each string
        while (strArr.value.length > 0) {
            result.push(DataFlowExpressionUtils.parseNextScopeFromDsl(strArr));
        }
        return result;
    }

    public static parseNextValueFromADsl(strArr: IStringWrapper, isExpression?: boolean): string {
        const delimiterChar = !isExpression ? '\'' : ')';
        const startChar = delimiterChar === '\'' ? delimiterChar : '(';
        strArr.value = strArr.value.trim();
        if (strArr.value[0] !== startChar) {
            throw new Error(`string does not start with ${startChar}`);
        }

        let nextChar = 0;
        do {
            nextChar = strArr.value.indexOf(delimiterChar, nextChar + 1);
        } while (nextChar >= 0 && !DataFlowExpressionUtils.isArrayDelimiter(strArr.value, nextChar, isExpression));
        if (nextChar < 0) {
            throw new Error('invalid string. end quote required');
        }
        const value = strArr.value.slice(1, nextChar);
        strArr.value = strArr.value.slice(nextChar + 1).trim();
        if (strArr.value.startsWith(',')) {
            strArr.value = strArr.value.slice(1);
        }
        return value;
    }

    public static parseNextScopeFromDsl(strArr: IStringWrapper): string {
        const scopeSymbolMap = new Map<string, string>([
            ['(', ')'],
            ['[', ']'],
            ['{', '}']
        ]);
        const stringSymbols = [`'`, `"`];
        const startSymbols = Array.from(scopeSymbolMap.keys());
        const endSymbols = Array.from(scopeSymbolMap.values());
        strArr.value = strArr.value.trim();
        if (!startSymbols.includes(strArr.value[0])) {
            throw new Error(`scope does not start with ${Array.from(scopeSymbolMap.keys()).join(', ')}`);
        }
        const last = <T>(value: T[]): T => value[value.length - 1];
        const symbols = [strArr.value[0]];
        let currentPosition = 1;
        let inString = false;
        while (symbols.length && currentPosition < strArr.value.length) {
            const char = strArr.value.charAt(currentPosition);
            const lastSymbol = last(symbols);
            if (inString) {
                if (char === lastSymbol) {
                    inString = false;
                    symbols.pop();
                }
            } else {
                if (stringSymbols.includes(char)) {
                    inString = true;
                    symbols.push(char);
                } else if (startSymbols.includes(char)) {
                    symbols.push(char);
                } else if (endSymbols.includes(char)) {
                    if (char === scopeSymbolMap.get(lastSymbol)) {
                        symbols.pop();
                    } else {
                        throw new Error(`invalid string: '${strArr.value}'`);
                    }
                }
            }
            currentPosition += 1;
        }
        if (symbols.length === 0) {
            const value = strArr.value.slice(0, currentPosition);
            strArr.value = strArr.value.slice(currentPosition).trim();
            if (strArr.value.startsWith(',')) {
                strArr.value = strArr.value.slice(1);
            }
            return value;
        } else {
            throw new Error(`invalid string: '${strArr.value}'`);
        }
    }

    public static parseNextValueFromADslMap(strArr: IStringWrapper, isExpression?: boolean): string {
        const delimiterChar = !isExpression ? '\'' : ')';
        const startChar = delimiterChar === '\'' ? delimiterChar : '(';
        strArr.value = strArr.value.trim();
        if (strArr.value[0] !== startChar) {
            throw new Error(`string does not start with ${startChar}`);
        }

        let nextChar = 0;
        do {
            nextChar = strArr.value.indexOf(delimiterChar, nextChar + 1);
        } while (nextChar >= 0 && !DataFlowExpressionUtils.isArrayDelimiter(strArr.value, nextChar, isExpression));
        if (nextChar < 0) {
            throw new Error('invalid string. end quote required');
        }
        const value = strArr.value.slice(1, nextChar);
        strArr.value = strArr.value.slice(nextChar + 2).trim();
        if (strArr.value.startsWith('->')) {
            strArr.value = strArr.value.slice(2);
        } else if (strArr.value.startsWith(',')) {
            strArr.value = strArr.value.slice(1);
        }
        return value;
    }

    public static parseExpressionArrayInDsl(str: string, owner: Owned, expectedReturnType: string = StringDataFlowType.TYPE, escapeValue: boolean = false): DataFlowExpression[] {
        const strArr: IStringWrapper = {
            value: str
        };
        const result: DataFlowExpression[] = [];
        DataFlowExpressionUtils.validateDSLArray(strArr);
        // loop for fetching each string
        while (strArr.value.length > 0) {
            result.push(DataFlowExpressionUtils.unwrapExpressionConfigValue(strArr, owner, expectedReturnType, escapeValue));
        }
        return result;
    }

    public static parseStringMapInDsl(str: string): Map<string, string> {
        const strArr: IStringWrapper = {
            value: str
        };
        const map = new Map<string, string>();
        strArr.value = DataFlowExpressionUtils.validateDSLArray(strArr);
        strArr.value.split(DataFlowAdapterUtils.MAP_ARRAY_SPLITTER).map((rule) => {
            const splits = rule.split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER);
            map.set(splits[0].trim(), splits[1].trim());
        });
        return map;
    }

    public static parseExpressionMapInDsl(str: string, transformation: Transformation, expectedReturnType: string = StringDataFlowType.TYPE, escapeValue: boolean = false): DataFlowParameterConfig[] {
        const strArr: IStringWrapper = {
            value: str
        };
        const result: DataFlowParameterConfig[] = [];
        DataFlowExpressionUtils.validateDSLArray(strArr);
        // loop for fetching each string
        while (strArr.value.length > 0) {
            const newParam = new DataFlowParameterConfig();
            newParam.paramName = DataFlowExpressionUtils.unwrapExpressionConfigValue(strArr, transformation, expectedReturnType, escapeValue, true);
            newParam.paramValue = DataFlowExpressionUtils.unwrapExpressionConfigValue(strArr, transformation, expectedReturnType, escapeValue, true);
            result.push(newParam);
        }
        return result;
    }

    /**
     * isArrayDelimiter is designed to determine whether char[index] is a delimiter of a slice of codes: strArr
     * @param strArr        the code string
     * @param index         ending index of the slice
     * @param isExpression  whether the slice is an expression list like parameters or regular string list
     */
    public static isArrayDelimiter(strArr: string, index: number, isExpression: boolean): boolean {
        if (!isExpression) {
            return this._determineIfArrayDelimForString(strArr, index);
        } else {
            // for expressions make sure the parenthesis are balanced to make sure this is the delimiter
            let balance = 0;
            // don't check parenthesis inside quotes
            let checkChar = true;
            // quoteChar would be defined beneath another quoted span
            let quoteChar;

            for (let i = 0; i <= index; i++) {
                // skip escaped characters
                if (strArr[i] === '\\') {
                    i++;
                    continue;
                }

                // skip unquoted comments within the commented expression
                // /*/ should not be a valid comment, thus we search its closing from i + 1
                // and even we indexOf beyond its length, it would still return -1
                if (!quoteChar && strArr.indexOf('/*', i) === i && strArr.indexOf('*/', i + 1) !== -1) {
                    i = strArr.indexOf('*/', i + 1) + 1;
                    continue;
                }

                // handle string interpolation
                if (quoteChar === '"' && strArr[i] === '{') {
                    let interpolatedCheckChar = true;
                    let interpolatedQuoteChar;
                    for (i; i <= index; i++) {
                        if (strArr[i] === '\\') {
                            i++;
                            continue;
                        }
                        if ((interpolatedQuoteChar && strArr[i] === interpolatedQuoteChar) || (!interpolatedQuoteChar && (strArr[i] === '\'' || strArr[i] === '"'))) {
                            interpolatedCheckChar = !interpolatedCheckChar;
                            interpolatedQuoteChar = interpolatedQuoteChar ? null : strArr[i];
                        } else if (interpolatedCheckChar) {
                            balance += strArr[i] === '(' ? 1 : strArr[i] === ')' ? -1 : 0;
                            if (strArr[i] === '}') {
                                break;
                            }
                        }
                    }
                }
                if ((quoteChar && strArr[i] === quoteChar) || (!quoteChar && (strArr[i] === '\'' || strArr[i] === '"'))) {
                    checkChar = !checkChar;
                    quoteChar = quoteChar ? null : strArr[i];
                } else if (checkChar) {
                    balance += strArr[i] === '(' ? 1 : strArr[i] === ')' ? -1 : 0;
                }
                if (balance < 0) {
                    return false;
                }
            }
            return !quoteChar && balance === 0;
        }
    }

    public static extractStringConfigValue(config: string): string {
        return config.replace(/'/g, '');
    }

    public static escapeAndWrapConfigValue(expr: DataFlowExpression): string {
        if (expr.templateExpression) {
            // We shouldn't do any escaping besides newline for dynamic content -
            // wrapExpressionConfigValue will just return the normalized expression
            return DataFlowExpressionUtils.escapeAllNewlineInDslString(DataFlowExpressionUtils.wrapExpressionConfigValue(expr));
        }

        expr.expression = DataFlowExpressionUtils.escapeBackslashInDslString(expr.expression);
        const wrappedExpr = DataFlowExpressionUtils.escapeAllNewlineInDslString(DataFlowExpressionUtils.wrapExpressionConfigValue(expr));

        // Unescape here, we don't want to change the actual expr object
        expr.expression = DataFlowExpressionUtils.unescapeBackslashInDslString(expr.expression);
        return wrappedExpr;
    }

    public static unescapeConfigValue(expr: DataFlowExpression): void {
        expr.expression = DataFlowExpressionUtils.unescapeAllNewlineInDslString(expr.expression);
        expr.expression = DataFlowExpressionUtils.unescapeBackslashInDslString(expr.expression);
    }

    public static wrapExpressionConfigValue(expression: DataFlowExpression, returnType: string = StringDataFlowType.TYPE, skipEscaping: boolean = false): string {
        let normalizedExpression = DataFlowExpressionUtils.defaultEmptyExpressions(expression, returnType);
        if (expression.templateExpression) {
            return `(${normalizedExpression})`;
        } else {
            if (returnType === StringDataFlowType.TYPE) {
                if (skipEscaping && normalizedExpression.startsWith('{') && normalizedExpression.endsWith('}')) {
                    normalizedExpression = normalizedExpression.substr(1, normalizedExpression.length - 2);
                }
                return DataFlowExpressionUtils.interpolateExpression(DataFlowExpressionUtils.escapeSingleQuoteInDslString(normalizedExpression));
            }

            if (returnType === TimeStampDataFlowType.TYPE && normalizedExpression) {
                return `(toTimestamp(${new Date(normalizedExpression).getTime()}L))`;
            }

            return normalizedExpression;
        }
    }

    public static wrapExpressionArrayConfigValue(
        expressionArray: DataFlowExpressionArray,
        returnType: string = StringDataFlowType.TYPE,
        skipEscaping: boolean = false
    ): string {
        return expressionArray.templateExpression
            ? `(${DataFlowExpressionUtils.defaultEmptyExpressions(expressionArray, ArrayDataFlowType.TYPE)})`
            : `[${expressionArray.expressions.map((col) => DataFlowExpressionUtils.wrapExpressionConfigValue(col, returnType, skipEscaping)).join(',')}]`;
    }

    public static wrapExpressionMapConfigValue(
        expressionMap:
            | Record<string, string | number | boolean | DataFlowExpression>
            | Map<string, string | number | boolean | DataFlowExpression>,
        skipEscaping: boolean = false
    ): string {
        const entries = isMap(expressionMap) ? expressionMap.entries() : Object.entries(expressionMap);
        const records: string[] = [];
        for (const [key, valueExpression] of entries) {
            let value: string;
            if (valueExpression instanceof DataFlowExpression) {
                value = this.wrapExpressionConfigValue(valueExpression, valueExpression.requiredReturnType, skipEscaping);
            } else {
                switch (typeof valueExpression) {
                    case 'string':
                        value = `'${valueExpression}'`;
                        break;
                    default:
                        value = String(valueExpression);
                }
            }
            records.push(`'${key}' -> ${value}`);
        }
        return records.length ? `[${records.join(', ')}]` : '';
    }

    public static interpolateExpression(expression: string): string {
        // interpolation format is @`mytext ${myVariable}`
        if (expression.startsWith('$`') && expression.endsWith('`')) {
            expression = expression.substring(2, expression.length - 1);
            const interpolationSyntax: RegExp = /@{[^@]*}/g;
            const stringParts = [];
            let currentIndex = 0;
            let match = null;
            do {
                match = interpolationSyntax.exec(expression);
                if (match && match[0]) {
                    if (match.index > currentIndex) {
                        stringParts.push(`'${expression.substring(currentIndex, match.index)}'`);
                    }
                    currentIndex = match.index + match[0].length;
                    if (match[1]) {
                        stringParts.push(expression.substring(match.index + 2, currentIndex - 1));
                    }
                }
            } while (match);
            if (currentIndex < expression.length) {
                stringParts.push(`'${expression.substring(currentIndex, expression.length)}'`);
            }
            return stringParts.length === 1 ? stringParts[0] : stringParts.length > 1 ? `(concat(${stringParts.join(',')}))` : `'${expression}'`;
        } else {
            return `'${expression}'`;
        }
    }

    public static defaultEmptyExpressions(expression: DataFlowExpression, returnType: string = StringDataFlowType.TYPE): string {
        let defaultValue = '';
        switch (returnType) {
            case BooleanDataFlowType.TYPE:
                defaultValue = 'false';
                break;
            case IntegerDataFlowType.TYPE:
                defaultValue = '0';
                break;
            default:
                defaultValue = '';
        }
        return expression.unserializedExpression || expression.expression || defaultValue;
    }

    public static unwrapExpressionConfigValue(expressionStr: IStringWrapper | string, owner: Owned,
        expectedReturnType: string = StringDataFlowType.TYPE, escapeValue: boolean = false, isMap: boolean = false): DataFlowExpression {
        const strWrapper: IStringWrapper = typeof expressionStr === 'object' ? expressionStr : { value: expressionStr };
        strWrapper.value = strWrapper.value.trim();
        const isExpression = strWrapper.value[0] === '(';
        const expValue = !isMap && (isExpression || expectedReturnType === StringDataFlowType.TYPE)
            ? DataFlowExpressionUtils.parseNextValueFromADsl(strWrapper, isExpression)
            : isMap && (isExpression || expectedReturnType === StringDataFlowType.TYPE) ? DataFlowExpressionUtils.parseNextValueFromADslMap(strWrapper, isExpression)
            : strWrapper.value;
        const expression = Owned.create<DataFlowExpression>(DataFlowExpression.MODEL, owner);
        expression.requiredReturnType = expectedReturnType;

        if (isExpression && expectedReturnType === TimeStampDataFlowType.TYPE && expValue.match('toTimestamp\\([0-9]{13}L\\)')) {
            expression.expression = new Date(Number(expValue.slice(12, 25))).toString();
            return expression;
        }

        if (isExpression) {
            expression.templateExpression = expValue;
            expression.expression = expValue;
        } else {
            expression.expression = DataFlowExpressionUtils.unescapeSingleQuoteInDslString(expValue);
            // This is the scenario where we pass columns names instead of columns references (e.g. keyColumns), this breaks references when unwraping column names with special characters.
            if (escapeValue && DataFlowUtils.needToEscape(expression.expression)) {
                expression.expression = `{${expression.expression}}`;
            }
        }
        return expression;
    }

    public static unwrapExpressionArrayConfigValue(
        expressionStr: IStringWrapper | string,
        owner: Owned,
        expectedReturnType: string = StringDataFlowType.TYPE,
        escapeValue: boolean = false
    ): DataFlowExpressionArray {
        const strWrapper: IStringWrapper = typeof expressionStr === 'object' ? expressionStr : { value: expressionStr };
        strWrapper.value = strWrapper.value.trim();
        const isExpression = strWrapper.value.startsWith('(') && strWrapper.value.endsWith(')');
        const expValue = isExpression
            ? DataFlowExpressionUtils.parseNextValueFromADsl(strWrapper, isExpression)
            : strWrapper.value;
        const expressionArray = Owned.create<DataFlowExpressionArray>(DataFlowExpressionArray.MODEL, owner);
        expressionArray.isTemplatable = true;

        if (isExpression) {
            expressionArray.templateExpression = expValue;
            expressionArray.expression = expValue;
        } else if (strWrapper.value.startsWith('[') && strWrapper.value.endsWith(']')) {
            expressionArray.expressions = DataFlowExpressionUtils.parseExpressionArrayInDsl(strWrapper.value, owner, expectedReturnType, escapeValue);
        }
        return expressionArray;
    }

    private static _determineIfArrayDelimForString(strArr: string, index: number): boolean {
        if (index-- <= 0) {
            return false;
        }
        // in a dsl string, the array delimiter for a non-expression is a single quote
        // however, the dsl string can also contain escaped single quotes and escaped backslashes (e.g 'abcd \\\\ \\'')
        // since this character (at index) is a single quote, count the number of backslashes before this quote to determine if this is an array delimiter or an escaped quote
        let backslashCount = 0;
        while (index >= 0 && strArr[index--] === '\\') {
            backslashCount++;
        }

        // 0 or even count implies this is indeed an array delimit
        // odd count implies this is an escaped quote, not an array delim
        return ((backslashCount % 2) === 0);
    }

    private static _escapeNewLineChars(expression: string): string {
        return expression
            .replace(/\n/g, DataFlowExpressionUtils._NEW_LINE_N_CONSTANT)
            .replace(/\r/g, DataFlowExpressionUtils._NEW_LINE_R_CONSTANT)
            .replace(/\'/g, DataFlowExpressionUtils._SINGLE_QUOTE_CONSTANT);
    }

    private static _unEscapeNewLineChars(expression: string): string {
        return expression
            .replace(new RegExp(DataFlowExpressionUtils._SINGLE_QUOTE_CONSTANT, 'g'), '\'')
            .replace(new RegExp(DataFlowExpressionUtils._NEW_LINE_R_CONSTANT, 'g'), '\r')
            .replace(new RegExp(DataFlowExpressionUtils._NEW_LINE_N_CONSTANT, 'g'), '\n');
    }
}
